import { RRule } from 'rrule';
import { SchedulerResponse } from '../types/scheduler.types';
import { EventResponse } from '../types/event.types';

const HOUR_MS = 60 * 60 * 1000;

// event.startTime/endTime and the event's recurrence day are wall-clock
// values in the schedule's *configured* timezone (offSet hours from UTC) -
// not the device's own system timezone. All of the helpers below derive
// "now"/"today" from the real UTC instant shifted by that offset, so the
// device's own clock timezone never affects the result.
const getScheduleLocalNow = (offSet: number) =>
  new Date(Date.now() + offSet * HOUR_MS);

// Real UTC instant for "today" (schedule-local calendar date) at the given
// schedule-local wall-clock time.
const setScheduleTimeToToday = (time: string, offSet: number) => {
  const local = getScheduleLocalNow(offSet);
  const [hours, mins, secs] = time.split(':').map(Number);
  const localMs = Date.UTC(
    local.getUTCFullYear(),
    local.getUTCMonth(),
    local.getUTCDate(),
    hours,
    mins,
    secs || 0,
    0,
  );
  return new Date(localMs - offSet * HOUR_MS);
};

const getEventSchedules = (event: EventResponse, offSet: number) => {
  const { presentationSchedule } = event;

  const rruleOptions = {
    ...presentationSchedule,
    dtstart: new Date(presentationSchedule.dtstart),
    ...(presentationSchedule.until && {
      until: new Date(presentationSchedule.until),
    }),
  };

  delete rruleOptions.bymonthday;
  const rule = new RRule(rruleOptions);

  // Occurrences always land on naive UTC-midnight of some Y-M-D (see
  // rRuleGenerator), so rather than windowing by a shifted day boundary
  // (which misaligns with that naive UTC-midnight convention once the
  // offset is negative enough to push it into the previous UTC day),
  // check directly whether schedule-local "today"'s naive-UTC-midnight
  // instant is itself an occurrence - a single-point inclusive query
  // that's correct regardless of the offset's sign or magnitude.
  const local = getScheduleLocalNow(offSet);
  const todayMidnightNaiveUtc = new Date(
    Date.UTC(
      local.getUTCFullYear(),
      local.getUTCMonth(),
      local.getUTCDate(),
      0,
      0,
      0,
      0,
    ),
  );
  return rule.between(todayMidnightNaiveUtc, todayMidnightNaiveUtc, true);
};

interface SchedulePresentationsProps {
  scheduler: SchedulerResponse;
  cacheInterval: number;
}

export default ({ scheduler }: SchedulePresentationsProps) => {
  const { events, offSet = 0 } = scheduler;

  if (!events.length) {
    return scheduler.defaultPresentation;
  }

  // Cache current time to avoid multiple Date() calls
  const currentDateAndTime = new Date();

  for (const event of events) {
    const schedules = getEventSchedules(event, offSet);

    if (schedules.length) {
      const actualStartTime = setScheduleTimeToToday(event.startTime, offSet);
      const actualEndTime = setScheduleTimeToToday(event.endTime, offSet);

      if (
        currentDateAndTime >= actualStartTime &&
        currentDateAndTime < actualEndTime
      ) {
        return event.presentation;
      }
    }
  }

  return scheduler.defaultPresentation;
};
